import useFileSaver from '@hooks/useFileSaver';
import useRequest, { IDataRequest } from '@hooks/useRequest';
import { ICliente } from '@model/Cliente';
import { IContratoCliente } from '@model/ContratoCliente';
import { EnumEmpresa } from '@model/Empresa';
import IFatura from '@model/Fatura';
import IFaturaPagamento from '@model/FaturaPagamento';
import ILinha from '@model/Linha';
import { EnumOperadora } from '@model/Operadora';
import React from 'react';

export interface IRelatorioConferenciaChipFilter {
    ddd?: string
    numeroSemMascara?: string
    empresaId: EnumEmpresa
    operadoraId: EnumOperadora
}

export interface IInadimplenciaFilter {
    empresaId?: string
}

export interface IInadimplenciaFaturaFilter {
    empresaId?: string
    competencia?: string
    modoCompetencia?: number
    whatsappEnvio?: string
}

export interface IInadimplenciaRecord extends ICliente {
    inadimplenciaTotal: number
}

export interface IInadimplenciaFaturaRecord extends IFatura {
}

export interface IInadimplenciaFaturaSummary {
    count: number,
    sumSaldo: number
    sumValorPago: number
}

export interface IPagamentoFilter {
    empresaId?: number
    competenciaPagamento: string
    competenciaReferencia: string
    formasPagamento?: number[]
    usuarioId?: number
    remetenteWhatsapp?: string
}

export interface IPagamentoSummary {
    count: number,
    sumValor: number
}

export interface IPagamentoRecord extends IFaturaPagamento {
}

export interface IConsumoInternetFilter {
    competencia: string
    ddd?: string
    numeroSemMascara?: string
    empresaId: EnumEmpresa
    operadoraId: EnumOperadora
    cliente: string
    numeroContrato: number
}

export interface IConsumoInternetRecord extends ILinha {
    competencia: string
    consumo: number
    clienteId: number
    clienteNome: string
}

export interface INovosInadimplentesFilter {
    competencia: string
    remetenteWhatsapp?: string
}

export interface INovosInadimplentesRecord extends ICliente {
    totalInadimplente: number
}

export interface INovosInadimplentesSummary {
    count: number,
    sumValor: number
}

export interface IRelatorioStatusLinhaFilter {
    ddd: number
    telefone: number
    contratoOperadoraId: number
    numerosContrato: string
}

export interface IRelatorioStatusLinhaSummary {
    count: number
}

const controller = '/Relatorio';

export function useRelatorioService () {
    const _request = useRequest();
    const fileSaver = useFileSaver();


    const listarConferenciaChip = React.useCallback((request: IDataRequest<IRelatorioConferenciaChipFilter>) => {
        return _request.getData<ILinha[]>(`${controller}/ListarConferenciaChip`, request);
    }, [_request]);

    const listarConferenciaChipContratos = React.useCallback((linhaId: number) => {
        return _request.get<IContratoCliente[]>(`${controller}/ListarConferenciaChipContratos/${linhaId}`);
    }, [_request]);

    const listarInadimplencia = React.useCallback((request: IDataRequest<IInadimplenciaFilter>) => {
        return _request.getData<IInadimplenciaRecord[]>(`${controller}/ListarInadimplencia`, request);
    }, [_request]);

    const listarInadimplenciaFatura = React.useCallback((request: IDataRequest<IInadimplenciaFaturaFilter>) => {
        return _request.getData<IInadimplenciaFaturaRecord[]>(`${controller}/ListarInadimplenciaFatura`, request);
    }, [_request]);

    const obterInadimplenciaFaturaSumario = React.useCallback((request: IInadimplenciaFaturaFilter) => {
        return _request.get<IInadimplenciaFaturaSummary>(`${controller}/obterInadimplenciaFaturaSumario`, {
            params: request
        });
    }, [_request]);

    const ListarInadimplenciaFaturas = React.useCallback((clienteId: number) => {
        return _request.get<IFatura[]>(`${controller}/ListarInadimplenciaFaturas/${clienteId}`);
    }, [_request]);


    const exportarInadimplenciaFatura = React.useCallback((request: IDataRequest<IInadimplenciaFaturaFilter>) => {
        return _request.download(`${controller}/ExportarInadimplenciaFatura`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Inadimplência-Fatura.csv`);
            });
    }, [_request, fileSaver]);

    const listarConsumoInternet = React.useCallback((request: IDataRequest<IConsumoInternetFilter>) => {
        return _request.getData<IConsumoInternetRecord[]>(`${controller}/ListarConsumoInternet`, request);
    }, [_request]);

    const listarConsumoInternetHistorico = React.useCallback((linhaId: number, competencia: string) => {
        return _request.get<IConsumoInternetRecord[]>(`${controller}/ListarConsumoInternetHistorico/${linhaId}/${competencia}`);
    }, [_request]);

    const exportarConsumoInternet = React.useCallback((request: IDataRequest<IConsumoInternetFilter>) => {
        return _request.download(`${controller}/ExportarConsumoInternet`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Consumo-Internet.csv`);
            });
    }, [_request, fileSaver]);


    
    //STATUS LINHA
    const listarStatusLinhaCliente = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.getReport<ILinha[]>(`${controller}/listarStatusLinhaCliente`, request);
    }, [_request]);

    const listarStatusLinhaFuncionario = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.getReport<ILinha[]>(`${controller}/listarStatusLinhaFuncionario`, request);
    }, [_request]);

    const listarStatusLinhaSemContrato = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.getReport<ILinha[]>(`${controller}/listarStatusLinhaSemContrato`, request);
    }, [_request]);

    const sumarizarStatusLinhaCliente = React.useCallback((request: IRelatorioStatusLinhaFilter) => {
        return _request.get<IRelatorioStatusLinhaSummary>(`${controller}/sumarizarStatusLinhaCliente`, { params: request});
    }, [_request]);

    const sumarizarStatusLinhaFuncionario = React.useCallback((request: IRelatorioStatusLinhaFilter) => {
        return _request.get<IRelatorioStatusLinhaSummary>(`${controller}/sumarizarStatusLinhaFuncionario`, { params: request});
    }, [_request]);

    const sumarizarStatusLinhaSemContrato = React.useCallback((request: IRelatorioStatusLinhaFilter) => {
        return _request.get<IRelatorioStatusLinhaSummary>(`${controller}/sumarizarStatusLinhaSemContrato`, { params: request});
    }, [_request]);
    
    const exportarStatusLinhaCliente = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.download(`${controller}/ExportarStatusLinhaCliente`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Status-Linhas-Clientes.csv`);
            });
    }, [_request, fileSaver]);

    const exportarStatusLinhaFuncionario = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.download(`${controller}/ExportarStatusLinhaFuncionario`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Status-Linhas-Funcionarios.csv`);
            });
    }, [_request, fileSaver]);

    const exportarStatusLinhaSemContrato = React.useCallback((request: IDataRequest<IRelatorioStatusLinhaFilter>) => {
        return _request.download(`${controller}/ExportarStatusLinhaSemContrato`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Status-Linhas-Sem-Contratos.csv`);
            });
    }, [_request, fileSaver]);
    
    //NOVOS INADIMPLENTES
    const listarNovosInadimplentes = React.useCallback((request: IDataRequest<INovosInadimplentesFilter>) => {
        return _request.getData<INovosInadimplentesRecord[]>(`${controller}/listarNovosInadimplentes`, request);
    }, [_request]);

    const sumarizarNovosInadimplentes = React.useCallback((request: INovosInadimplentesFilter) => {
        return _request.get<INovosInadimplentesSummary>(`${controller}/SumarizarNovosInadimplentes`, {
            params: request
        });
    }, [_request]);

    const exportarNovosInadimplentes = React.useCallback((request: IDataRequest<INovosInadimplentesFilter>) => {
        return _request.download(`${controller}/ExportarNovosInadimplentes`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Novos-Inadimplentes.csv`);
            });
    }, [_request, fileSaver]);

    //PAGAMENTOS
    const listarPagamentos = React.useCallback((request: IDataRequest<IPagamentoFilter>) => {
        return _request.getReport<IPagamentoRecord[], IPagamentoSummary>(`${controller}/listarPagamentos`, request);
    }, [_request]);

    const sumarizarPagamentos = React.useCallback((request: IPagamentoFilter) => {
        return _request.get<IPagamentoSummary>(`${controller}/SumarizarPagamentos`, {
            params: request
        });
    }, [_request]);

    const exportarPagamentos = React.useCallback((request: IDataRequest<IPagamentoFilter>) => {
        return _request.download(`${controller}/ExportarPagamentos`, {
            params: request
        })
            .then(response => { 
                fileSaver.save(response, `Novos-Inadimplentes.csv`);
            });
    }, [_request, fileSaver]);

    return React.useMemo(() => ({
        listarConferenciaChip, listarConferenciaChipContratos,
        listarInadimplencia, ListarInadimplenciaFaturas,
        listarInadimplenciaFatura, obterInadimplenciaFaturaSumario, exportarInadimplenciaFatura,
        listarConsumoInternet, listarConsumoInternetHistorico, exportarConsumoInternet,
        listarNovosInadimplentes, sumarizarNovosInadimplentes, exportarNovosInadimplentes,
        listarPagamentos, sumarizarPagamentos, exportarPagamentos,
        listarStatusLinhaCliente, listarStatusLinhaFuncionario, listarStatusLinhaSemContrato,
        sumarizarStatusLinhaCliente, sumarizarStatusLinhaFuncionario, sumarizarStatusLinhaSemContrato,
        exportarStatusLinhaCliente, exportarStatusLinhaFuncionario, exportarStatusLinhaSemContrato,
    }), [listarConferenciaChip, listarConferenciaChipContratos, listarInadimplencia, ListarInadimplenciaFaturas, listarInadimplenciaFatura, obterInadimplenciaFaturaSumario, exportarInadimplenciaFatura, listarConsumoInternet, listarConsumoInternetHistorico, exportarConsumoInternet, listarNovosInadimplentes, sumarizarNovosInadimplentes, exportarNovosInadimplentes, listarPagamentos, sumarizarPagamentos, exportarPagamentos, listarStatusLinhaCliente, listarStatusLinhaFuncionario, listarStatusLinhaSemContrato, sumarizarStatusLinhaCliente, sumarizarStatusLinhaFuncionario, sumarizarStatusLinhaSemContrato, exportarStatusLinhaCliente, exportarStatusLinhaFuncionario, exportarStatusLinhaSemContrato]);
}